/**
 * Pixel density: how finely characters are drawn against the world (option C, adopted 2026-10-06).
 *
 * The rule — two grids, nothing in between, and the UI on its own plane:
 * - G1, the world grid: one art pixel (`px` CSS pixels on screen). Ground (whose texels are 2–4 art
 *   pixels), props, buildings, contact and cast shadows, and the skill effects that belong to the
 *   world (bursts, sparks, rings, circles, projectiles, ground decals) are drawn on it.
 * - G2, the figure grid: half an art pixel. Every character (player, NPC, monster, beast) is drawn on
 *   it at one density — body, clothes, hair, face, hands and weapon alike, nothing finer — with
 *   one-G2-pixel outlines and dark lines. Effects that cling to a figure (weapon trails, the glow and
 *   aura rims, qi flares, buff spirals, frost on a blade) step in G2 pixels too.
 * - Feet (the figure's anchor) snap to G1, so they meet the tiles and the shadows.
 * - The UI (labels, numbers, windows) is drawn at screen resolution, on no world grid.
 *
 * Characters are authored at 2× (G2). The figure's art-pixel parts (body, clothes, hair, headwear)
 * are brought onto G2 by Scale2x plus a pass that thins every one-art-pixel dark line to one G2 pixel
 * (`thinLines`, pixel.ts); the face, hands and weapon are drawn on G2 already. The code-drawn beasts
 * go the same way (`toFigureGrid`).
 *
 * Two modes, chosen per page load by the graphics tier (graphics.ts) and fixed for the page's life
 * (changing it needs the other bake set, so it applies after a reload):
 * - `c`: option C as above (tiers ปานกลาง, สูง, สูงมาก);
 * - `1x`: everything on G1 — the world picture at one canvas pixel per art pixel, and each figure
 *   frame the C frame brought down ÷2 by majority colour with its outline mended (`toArtRes`), the
 *   same frames the derived 1× bake holds (tiers ต่ำมาก, ต่ำ). Beasts keep their 1× art.
 * `?density=1x` (or `c`) in the page's URL overrides the tier, for testing.
 *
 * The figure workers take the page's mode from their first message (`densityMessage`).
 */
export type Density = 'c' | '1x';

const isDensity = (d: unknown): d is Density => d === 'c' || d === '1x';

/** `?density=` in the page's URL, if any. */
export function densityFromUrl(): Density | null {
  if (typeof location === 'undefined' || typeof document === 'undefined') return null;
  const d = new URLSearchParams(location.search).get('density');
  return isDensity(d) ? d : null;
}

let mode: Density = densityFromUrl() ?? 'c';

/** The density mode this page (or worker) draws in. */
export const density = (): Density => mode;
/** Option C: figures on their own half-art-pixel grid, the world on whole art pixels. */
export const densityC = (): boolean => mode === 'c';
/** Everything on whole art pixels (the low tiers). */
export const density1x = (): boolean => mode === '1x';

/**
 * Sets the page's mode (graphics.ts, once at boot, before any figure is drawn or worker started).
 * A `?density=` in the URL wins.
 */
export function setDensity(d: Density): void {
  mode = densityFromUrl() ?? d;
}

/** What the main thread tells a new figure worker, so it draws in the page's mode. */
export const densityMessage = (): { density: Density } => ({ density: mode });

/** Takes the mode from a `densityMessage` (in a worker, or a tool); true if the message was one. */
export function takeDensity(data: unknown): boolean {
  if (!data || typeof data !== 'object' || !('density' in data) || 'id' in data) return false;
  const d = (data as { density: unknown }).density;
  mode = isDensity(d) ? d : 'c';
  return true;
}

/** The step of an effect that clings to a figure, in art pixels: one G2 pixel under option C. */
export const clingStep = (): number => (mode === 'c' ? 0.5 : 1);

/**
 * The bake set a page loads (render/rig/bake.ts): `2x` frames for option C, the `1x` set derived
 * from them for the 1x mode.
 */
export const bakeSet = (): '2x' | '1x' => (mode === '1x' ? '1x' : '2x');
